import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ServiceFinder = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessType: '',
    serviceCategory: '',
    budget: '',
    timeline: ''
  });

  const businessTypes = [
    { value: 'startup', label: 'Startup / Nouvelle Entreprise' },
    { value: 'sme', label: 'PME (10-50 employés)' },
    { value: 'corporate', label: 'Grande Entreprise (50+ employés)' },
    { value: 'ngo', label: 'ONG / Organisation Internationale' },
    { value: 'individual', label: 'Particulier' }
  ];

  const serviceCategories = [
    { value: 'real-estate', label: 'Immobilier & Location' },
    { value: 'vehicles', label: 'Transport & Véhicules' },
    { value: 'legal', label: 'Services Juridiques' },
    { value: 'it', label: 'Solutions IT' },
    { value: 'events', label: 'Événements' },
    { value: 'supplies', label: 'Fournitures' },
    { value: 'multiple', label: 'Services Multiples' }
  ];

  const budgetRanges = [
    { value: 'small', label: 'Moins de 5,000 USD' },
    { value: 'medium', label: '5,000 - 20,000 USD' },
    { value: 'large', label: '20,000 - 50,000 USD' },
    { value: 'enterprise', label: 'Plus de 50,000 USD' },
    { value: 'flexible', label: 'Budget Flexible' }
  ];

  const timelines = [
    { value: 'urgent', label: 'Urgent (Moins d\'1 semaine)' },
    { value: 'short', label: 'Court terme (1-4 semaines)' },
    { value: 'medium', label: 'Moyen terme (1-3 mois)' },
    { value: 'long', label: 'Long terme (3+ mois)' },
    { value: 'ongoing', label: 'Service Continu' }
  ];

  const recommendations = {
    'startup-real-estate': {
      title: 'Pack Startup Immobilier',
      services: ['Bureau Flexible', 'Domiciliation', 'Support Administratif'],
      description: 'Solution complète pour démarrer votre entreprise avec un espace professionnel adapté',
      icon: 'Rocket'
    },
    'corporate-it': {
      title: 'Infrastructure IT Entreprise',
      services: ['Réseau Sécurisé', 'Support 24/7', 'Cloud Solutions'],
      description: 'Infrastructure technologique robuste pour grandes organisations',
      icon: 'Server'
    },
    'individual-vehicles': {
      title: 'Service VIP Personnel',
      services: ['Chauffeur Privé', 'Véhicule Premium', 'Disponibilité Flexible'],
      description: 'Transport de luxe avec chauffeur professionnel pour vos déplacements',
      icon: 'Crown'
    },
    'default': {
      title: 'Solution Personnalisée',
      services: ['Consultation Gratuite', 'Devis Sur Mesure', 'Support Dédié'],
      description: 'Nous créons une solution adaptée à vos besoins spécifiques',
      icon: 'Sparkles'
    }
  };

  const handleNext = () => { if (currentStep < 4) setCurrentStep(currentStep + 1); };
  const handleBack = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };
  const handleSubmit = () => { navigate('/contact', { state: { formData } }); };

  const getRecommendation = () => {
    const key = `${formData?.businessType}-${formData?.serviceCategory}`;
    return recommendations?.[key] || recommendations?.default;
  };

  const progress = (currentStep / 4) * 100;

  return (
    <section className="py-24 bg-white dark:bg-slate-900 relative">
      <div className="absolute inset-0 bg-white dark:bg-slate-900" />
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-900/10 to-transparent pointer-events-none" />

      {/* CONTAINER ÉLARGI : max-w-[1920px] */}
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">

        {/* Centré mais beaucoup plus large (max-w-6xl au lieu de 4xl) */}
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full mb-4">
              <Icon name="Search" size={18} className="text-amber-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Assistant Intelligent</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
              Trouvez votre <span className="text-blue-500">Solution Idéale</span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Répondez à 4 questions simples pour obtenir une recommandation immédiate adaptée à vos besoins.
            </p>
          </motion.div>

          {/* Progress Bar Large */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Progression</span>
              <span className="text-sm font-bold text-blue-400">{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-600 to-amber-400"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Form Card Wide & Tall */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-50 dark:bg-slate-800/60 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-[2rem] p-10 md:p-16 shadow-2xl min-h-[500px] flex flex-col justify-between"
          >
            <AnimatePresence mode="wait">
              {/* Step 1 */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-12 max-w-3xl mx-auto w-full"
                >
                  <div className="text-center">
                    <div className="w-24 h-24 bg-blue-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-blue-500/20 shadow-lg shadow-blue-900/20">
                      <Icon name="Building" size={48} className="text-blue-400" />
                    </div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">Type d'Organisation</h3>
                    <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">Qui êtes-vous ?</p>
                  </div>

                  <div className="bg-slate-100 dark:bg-slate-700/30 p-2 rounded-xl">
                    <Select
                      label="Sélectionnez votre structure"
                      placeholder="Choisir le type d'entreprise..."
                      options={businessTypes}
                      value={formData.businessType}
                      onChange={(value) => setFormData({ ...formData, businessType: value })}
                    // Ajoutez des classes custom ici si votre composant Select le permet pour l'adapter au fond sombre
                    />
                  </div>
                </motion.div>
              )}

              {/* Step 2 */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-12 max-w-3xl mx-auto w-full"
                >
                  <div className="text-center">
                    <div className="w-24 h-24 bg-amber-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-amber-500/20 shadow-lg shadow-amber-900/20">
                      <Icon name="Grid" size={48} className="text-amber-400" />
                    </div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">Catégorie de Service</h3>
                    <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">De quoi avez-vous besoin ?</p>
                  </div>

                  <div className="bg-slate-100 dark:bg-slate-700/30 p-2 rounded-xl">
                    <Select
                      label="Domaine d'activité"
                      placeholder="Choisir une catégorie..."
                      options={serviceCategories}
                      value={formData.serviceCategory}
                      onChange={(value) => setFormData({ ...formData, serviceCategory: value })}
                    />
                  </div>
                </motion.div>
              )}

              {/* Step 3 */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-12 max-w-3xl mx-auto w-full"
                >
                  <div className="text-center">
                    <div className="w-24 h-24 bg-green-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-green-500/20 shadow-lg shadow-green-900/20">
                      <Icon name="DollarSign" size={48} className="text-green-400" />
                    </div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">Budget Estimé</h3>
                    <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">Quelle est votre fourchette ?</p>
                  </div>

                  <div className="bg-slate-100 dark:bg-slate-700/30 p-2 rounded-xl">
                    <Select
                      label="Fourchette budgétaire"
                      placeholder="Définir votre budget..."
                      options={budgetRanges}
                      value={formData.budget}
                      onChange={(value) => setFormData({ ...formData, budget: value })}
                    />
                  </div>
                </motion.div>
              )}

              {/* Step 4 */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-12 w-full"
                >
                  {!formData.timeline ? (
                    <div className="max-w-3xl mx-auto w-full">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-purple-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-purple-500/20 shadow-lg shadow-purple-900/20">
                          <Icon name="Clock" size={48} className="text-purple-400" />
                        </div>
                        <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">Délai Souhaité</h3>
                        <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">Quand souhaitez-vous commencer ?</p>
                      </div>

                      <div className="bg-slate-100 dark:bg-slate-700/30 p-2 rounded-xl">
                        <Select
                          label="Urgence du projet"
                          placeholder="Sélectionner un délai..."
                          options={timelines}
                          value={formData.timeline}
                          onChange={(value) => setFormData({ ...formData, timeline: value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-1 rounded-[2.5rem] bg-gradient-to-r from-blue-500 via-purple-500 to-amber-500 shadow-2xl max-w-4xl mx-auto"
                    >
                      <div className="bg-white dark:bg-slate-900 rounded-[2.4rem] p-10 md:p-16 text-slate-900 dark:text-white h-full">
                        <div className="flex flex-col md:flex-row items-start gap-10">
                          <div className="w-24 h-24 rounded-3xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/30">
                            <Icon name={getRecommendation().icon} size={48} />
                          </div>
                          <div className="flex-1">
                            <div className="inline-block px-4 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-sm font-bold uppercase tracking-wide mb-4">Recommandé pour vous</div>
                            <h4 className="text-4xl font-bold mb-6">{getRecommendation().title}</h4>
                            <p className="text-slate-600 dark:text-slate-300 text-xl mb-10 leading-relaxed">{getRecommendation().description}</p>
                            <div className="flex flex-wrap gap-4">
                              {getRecommendation().services.map((service, index) => (
                                <span key={index} className="px-5 py-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 font-medium text-lg">
                                  {service}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-16 pt-10 border-t border-slate-200 dark:border-slate-700/50">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
                className="border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 disabled:opacity-30 px-8 py-4 text-xl rounded-xl"
              >
                Retour
              </Button>

              {currentStep < 4 && !formData.timeline ? (
                <Button
                  variant="default"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !formData.businessType) ||
                    (currentStep === 2 && !formData.serviceCategory) ||
                    (currentStep === 3 && !formData.budget)
                  }
                  iconName="ChevronRight"
                  iconPosition="right"
                  className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 px-10 py-4 text-xl font-bold rounded-xl"
                >
                  Suivant
                </Button>
              ) : (
                formData.timeline && (
                  <Button
                    variant="default"
                    onClick={handleSubmit}
                    iconName="Send"
                    iconPosition="right"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold shadow-lg shadow-amber-500/20 px-10 py-4 text-xl rounded-xl"
                  >
                    Obtenir mon Devis
                  </Button>
                )
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceFinder;